import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import { GET_PEER_POINTS_LEADERBOARD } from "../../api/query";
import { useAuth } from "../../context/AuthContext";
import Loader from "../../components/common/Loader";

const MONTHS = [
    { key: "1", name: "January" },
    { key: "2", name: "February" },
    { key: "3", name: "March" },
    { key: "4", name: "April" },
    { key: "5", name: "May" },
    { key: "6", name: "June" },
    { key: "7", name: "July" },
    { key: "8", name: "August" },
    { key: "9", name: "September" },
    { key: "10", name: "October" },
    { key: "11", name: "November" },
    { key: "12", name: "December" },
];

const YEARS = [
    { key: "2023", name: "2023" },
    { key: "2024", name: "2024" },
    { key: "2025", name: "2025" },
];

const PeerPointsLeaderboard = () => {
    const { userInfo } = useAuth();
    const organizationID = userInfo?.agencies?.id || '';

    // Get current month and year
    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonthName = MONTHS.find(m => m.key === currentMonth)?.name || "";

    const [selectedMonthKey, setSelectedMonthKey] = useState(currentMonth);
    const [selectedMonthName, setSelectedMonthName] = useState(currentMonthName);
    const [selectedYear, setSelectedYear] = useState(currentYear);

    // Fetch leaderboard data
    const { loading, data, refetch } = useQuery(GET_PEER_POINTS_LEADERBOARD, {
        variables: {
            agency_id: parseInt(organizationID),
            month: parseInt(selectedMonthKey),
            year: parseInt(selectedYear)
        },
        skip: !organizationID,
        fetchPolicy: 'no-cache'
    });

    const leaderboard = data?.get_peer_points_leaderboard?.leaderboard || [];

    // Refetch data when month or year changes
    useEffect(() => {
        if (organizationID) {
            refetch();
        }
    }, [selectedMonthKey, selectedYear, organizationID, refetch]);



    if (loading) {
        return <Loader />;
    }

    return (
        <div id="cts-respon-p-dv" className="w-full flex flex-col items-start justify-start py-40 px-20 mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full">
            {/* Header */}
            <h1 className="text-3xl font-bold mb-6 dark:text-white">Peer Points Leaderboard</h1>

            {/* Month and Year Dropdowns */}
            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                <div className="flex-1">
                    <SearchableInputsDropdown
                        label="Month"
                        placeHolder="Select Month"
                        options={MONTHS}
                        initialValue={selectedMonthName}
                        onSelect={(key: string, name: string) => {
                            setSelectedMonthKey(key);
                            setSelectedMonthName(name);
                        }}
                    />
                </div>
                <div className="flex-1">
                    <SearchableInputsDropdown
                        label="Year"
                        placeHolder="Select Year"
                        options={YEARS}
                        initialValue={selectedYear}
                        onSelect={(_key: string, name: string) => setSelectedYear(name)}
                    />
                </div>
            </div>

            {/* Leaderboard */}
            <div className="w-full bg-white dark:bg-slate-700 rounded-lg shadow-md border border-gray-200 dark:border-slate-600 overflow-hidden">
                {leaderboard.length > 0 ? (
                    <div className="overflow-x-auto peer-tabel-dv">
                        <table className="w-full mq991:w-[1000px]" cellPadding={0} cellSpacing={0}>
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-slate-600">
                                    <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">Rank</th>
                                    <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">Employee</th>
                                    <th className="text-right py-3 px-4 font-semibold text-sm dark:text-white">Points</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leaderboard.map((entry: any) => (
                                    <tr
                                        key={entry.employee_id}
                                        className="border-b border-gray-100 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600"
                                    >
                                        {/* Rank */}
                                        <td className="py-3 px-4 dark:text-white font-semibold">
                                            {entry.rank}
                                        </td>

                                        {/* Employee */}
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-3">
                                                {entry.employee_image_url ? (
                                                    <img
                                                        src={entry.employee_image_url}
                                                        alt={entry.employee_name}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                                                        <span className="text-white font-semibold text-xs">
                                                            {entry.employee_name?.charAt(0)?.toUpperCase() || "?"}
                                                        </span>
                                                    </div>
                                                )}
                                                <span className="dark:text-white">{entry.employee_name}</span>
                                            </div>
                                        </td>

                                        {/* Points */}
                                        <td className="py-3 px-4 text-right dark:text-white font-semibold">
                                            {entry.total_points}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="py-8 text-center">
                        <p className="text-gray-500 dark:text-gray-400">
                            No leaderboard data available for this period
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PeerPointsLeaderboard;
